import classNames from 'classnames';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import * as events from '../../lib/analytics/events';
import { centsToDollars } from '../../lib/util';
import styles from '../../styles/lineItem.module.scss';
import ErrorMessage from '../ErrorMessage';
import RemoveLineItemButton from '../RemoveLineItemButton';
import { useDispatch } from 'react-redux';
import CheckoutContext from '../../lib/CheckoutContext';
import { showNanoVariant } from '../NanoBanner/NanoBanner';
import NanoCouponLineItem from './CouponLineItemV2';
import { getFiGrowthBook } from '../../lib/growthbook';

interface CouponLineItemProps {
  allowRemoveCoupon?: boolean;
  appliedDiscountInCents?: number;
  couponCode: string;
  validationError?: string | null;
  appliedDiscountInMonths?: number;
}

export default function CouponLineItem({
  allowRemoveCoupon,
  appliedDiscountInCents,
  couponCode,
  validationError,
  appliedDiscountInMonths,
}: CouponLineItemProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { checkoutActions } = useContext(CheckoutContext);

  if (validationError) {
    return null;
  }

  const cartV2Enabled = showNanoVariant() || getFiGrowthBook().getFeatureValue('nano-ecom-test-1', false);
  if (cartV2Enabled) {
    return (
      <NanoCouponLineItem
        {...{ allowRemoveCoupon, appliedDiscountInCents, couponCode, validationError, appliedDiscountInMonths }}
      />
    );
  }

  const couponCodeApplied = !!appliedDiscountInCents || !!appliedDiscountInMonths;

  return (
    <div className={classNames(styles.lineItem, { [styles.noRemove]: !allowRemoveCoupon })}>
      {allowRemoveCoupon && (
        <div className={styles.removeButtonRow}>
          <RemoveLineItemButton
            onClick={() => {
              dispatch(checkoutActions.removeCoupon({}));
              events.cartPage.couponRemoved(couponCode);
            }}
          />
        </div>
      )}
      <div className={styles.itemRow}>
        <div className={classNames(styles.lineItemImage, styles.empty)}></div>
        <div className={styles.lineItemBody}>
          <div className={styles.lineItemPriceRows}>
            <div className={styles.lineItemPriceRow}>
              <div>
                <div>{couponCodeApplied ? t('promo_code_applied') : t('promo_code')}</div>
                <div>
                  <strong>{couponCode}</strong>
                </div>
                {!!appliedDiscountInMonths && (
                  <div className={styles.lineItemPrice}>
                    {appliedDiscountInMonths > 1
                      ? t('extra_months_free_total', { count: appliedDiscountInMonths })
                      : t('extra_month_free_total', { count: appliedDiscountInMonths })}
                  </div>
                )}
              </div>
              {!!appliedDiscountInCents && !appliedDiscountInMonths && (
                <>
                  {/* eslint-disable-next-line react/jsx-no-literals */}
                  <div className={styles.lineItemPrice}>-${centsToDollars(appliedDiscountInCents)}</div>
                </>
              )}
            </div>
            {!!validationError && (
              <div className={styles.lineItemPriceRow}>
                <ErrorMessage errors={[validationError]} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
